import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type Permission, WorkspacesApi } from '@up2stay/up2promo-api-client';

import { membershipsQueryKey } from '@/core/hooks/use-memberships-query';
import { useAuthStore } from '@/core/store/auth.store';

export const workspacePermissionsQueryKey = (
  workspaceId?: string,
  userId?: string,
) => ['workspace-permissions', workspaceId, userId] as const;

export function usePermissionsQuery(workspaceId?: string) {
  const user = useAuthStore((state) => state.user);
  const hydrated = useAuthStore((state) => state.hydrated);

  return useQuery({
    queryKey: workspacePermissionsQueryKey(workspaceId, user?.id),
    queryFn: async () => {
      if (!workspaceId) return [] as Permission[];
      const response = await WorkspacesApi.getMyPermissions({
        path: { id: workspaceId },
      } as never);
      return response.data?.permissions ?? [];
    },
    enabled: hydrated && !!user?.id && !!workspaceId,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useInvalidateWorkspacePermissions() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return (workspaceId?: string) => {
    void queryClient.invalidateQueries({
      queryKey: membershipsQueryKey(user?.id),
    });
    if (workspaceId) {
      void queryClient.invalidateQueries({
        queryKey: workspacePermissionsQueryKey(workspaceId, user?.id),
      });
    }
  };
}
